import React, { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { Sparkles, Send } from "lucide-react";
import { api } from "../../lib/api";

const DEFAULT_PROMPTS = [
  "What should I do next?",
  "Why do you need this information?",
  "Which package fits us best?",
  "Explain IFRS S2 in plain English",
];

export function AdviserPanel({ sessionId, prompts = DEFAULT_PROMPTS, intro }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        intro ||
        "Hi — I'm your Lestari onboarding adviser. I'll explain why each step matters and recommend your next best action. Ask me anything.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      const { data } = await api.post("/onboarding/ai/adviser", { session_id: sessionId, question: text });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the adviser just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="adviser-panel">
      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-3" data-testid="adviser-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[88%] rounded-sm px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-accent text-black" : "bg-surface border border-border"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-surface border border-border px-3.5 py-2.5 rounded-sm">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
                <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-5 pb-2 pt-3 border-t border-border">
          <div className="label-eyebrow mb-2">Try asking</div>
          <div className="flex flex-wrap gap-1.5">
            {prompts.map((p) => (
              <button
                key={p}
                onClick={() => send(p)}
                className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-surface-hover transition-colors text-left"
                data-testid="adviser-prompt"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-border p-3 flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the adviser…"
          rows={2}
          className="text-sm resize-none"
          data-testid="adviser-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
        />
        <Button onClick={() => send(input)} disabled={busy || !input.trim()} variant="primary" data-testid="adviser-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
